import React, { Suspense } from 'react';
import axios from 'axios';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import { Toaster as HotToaster } from 'react-hot-toast';
import ProtectedRoute from './components/ProtectedRoute';
import { installAdminAuthInterceptor } from './api/adminAuthInterceptor';
import { installCustomerAuthInterceptor } from './api/customerAuthInterceptor';
import './styles/bba-mobile.css';
import './styles/admin-contrast.css';
import './App.css';

// SECURITY (2026-09): customer session cookies are httpOnly — every axios
// request must carry credentials for the backend to see them. This is a
// no-op for admin calls (they authenticate via an explicit Bearer header).
axios.defaults.withCredentials = true;

// Install global 401 -> refresh interceptors once at module load.
// Admin is installed first so it gets first look at admin calls; the
// customer interceptor skips anything carrying an admin Authorization
// header or hitting an /admin/ path.
installAdminAuthInterceptor();
installCustomerAuthInterceptor();

// Public Pages
import LandingPage from './pages/LandingPage';
import TermsPage from './pages/TermsPage';
import AuthPage from './pages/customer/AuthPage';
import AuthCallback from './pages/customer/AuthCallback';
import CompleteGoogleProfile from './pages/customer/CompleteGoogleProfile';
import VerifyEmail from './pages/customer/VerifyEmail';
import ForgotPassword from './pages/customer/ForgotPassword';
import ResetPassword from './pages/customer/ResetPassword';

// Customer Pages
import HomePage from './pages/customer/HomePage';
import BookingFlow from './pages/customer/BookingFlow';
import BookingsList from './pages/customer/BookingsList';
import BookingDetails from './pages/customer/BookingDetails';
import BookingSubmitted from './pages/customer/BookingSubmitted';
import PaymentPage from './pages/customer/PaymentPage';
import PaymentResult from './pages/customer/PaymentResult';
import PaymentSuccessRedirect from './pages/customer/PaymentSuccessRedirect';
import BookingConfirmed from './pages/customer/BookingConfirmed';
import ProfilePage from './pages/customer/ProfilePage';

// Admin Pages — lazy-loaded so their JS chunks are only fetched when a
// user actually navigates to /admin/*. Customers browsing the booking
// flow never download the admin dashboard, analytics, DB tools, etc.
const AdminLogin = React.lazy(() => import('./pages/admin/AdminLogin'));
const AdminForgotPassword = React.lazy(() => import('./pages/admin/AdminForgotPassword'));
const AdminLayout = React.lazy(() => import('./pages/admin/AdminLayout'));
const AdminDashboard = React.lazy(() => import('./pages/admin/AdminDashboard'));
const BookingsManagement = React.lazy(() => import('./pages/admin/BookingsManagement'));
const BookingSeries = React.lazy(() => import('./pages/admin/BookingSeries'));
const SeriesDetail = React.lazy(() => import('./pages/admin/SeriesDetail'));
const ManualBooking = React.lazy(() => import('./pages/admin/ManualBooking'));
const CustomerManagement = React.lazy(() => import('./pages/admin/CustomerManagement'));
const StaffManagement = React.lazy(() => import('./pages/admin/StaffManagement'));
const PricingManagement = React.lazy(() => import('./pages/admin/PricingManagement'));
const LocationsManagement = React.lazy(() => import('./pages/admin/LocationsManagement'));
const Analytics = React.lazy(() => import('./pages/admin/Analytics'));
const SystemSettings = React.lazy(() => import('./pages/admin/SystemSettings'));
const SecuritySettings = React.lazy(() => import('./pages/admin/SecuritySettings'));
const IntegrationsManagement = React.lazy(() => import('./pages/admin/IntegrationsManagement'));
const DbTools = React.lazy(() => import('./pages/admin/DbTools'));
const SettingsHolidays = React.lazy(() => import('./pages/admin/SettingsHolidays'));

// Customer-facing layouts stay eager — every visitor needs these immediately.
import CustomerLayout from './pages/customer/CustomerLayout';
import CustomerNavLayout from './pages/customer/CustomerNavLayout';
import PageLoader from './components/PageLoader';


/**
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH — always derive the redirect from window.location.origin.
 */
function AppRouter() {
  const location = useLocation();

  // Synchronous check: render AuthCallback BEFORE normal routes (and before
  // AuthContext tries to hydrate localStorage) when Google redirects back here.
  if (location.pathname === '/auth/google/callback') {
    return <AuthCallback />;
  }

  return (
    <Routes>
            {/* Customer-facing layout (public + post-login) — renders
                <SiteBanner scope="customer" /> once at the top of every
                customer page. Does NOT change any URL. */}
            <Route element={<CustomerLayout />}>
              {/* Public Routes */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/auth" element={<AuthPage />} />
              <Route path="/auth/google/complete-profile" element={<CompleteGoogleProfile />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/terms" element={<TermsPage />} />

              {/* Customer Routes */}
              <Route path="/book" element={<BookingFlow />} />
              <Route path="/booking-submitted/:bookingId" element={<BookingSubmitted />} />
              <Route path="/payment/:bookingId" element={<PaymentPage />} />
              <Route path="/payment/success" element={<PaymentSuccessRedirect />} />
              <Route path="/payment-result" element={<PaymentResult />} />
              <Route path="/booking-confirmed/:bookingId" element={<BookingConfirmed />} />

              {/* Customer "hub" pages — always show the bottom tab bar */}
              <Route element={<CustomerNavLayout />}>
                <Route path="/home" element={<HomePage />} />
                <Route path="/bookings" element={<BookingsList />} />
                <Route path="/booking/:bookingId" element={<BookingDetails />} />
                <Route path="/profile" element={<ProfilePage />} />
              </Route>
            </Route>

            {/* Admin Routes with RBAC Protection — each lazy chunk gets its
                own Suspense boundary so the sidebar (AdminLayout) stays
                mounted while switching between nested admin pages; see the
                inner <Suspense> around <Outlet/> in AdminLayout.js. */}
            <Route path="/admin/login" element={<Suspense fallback={<PageLoader />}><AdminLogin /></Suspense>} />
            <Route path="/admin/forgot-password" element={<Suspense fallback={<PageLoader />}><AdminForgotPassword /></Suspense>} />
            <Route path="/admin" element={<Suspense fallback={<PageLoader />}><AdminLayout /></Suspense>}>
              <Route index element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="dashboard" element={<AdminDashboard />} />
              <Route path="bookings" element={<BookingsManagement />} />
              <Route path="bookings/series" element={<BookingSeries />} />
              <Route path="bookings/series/:id" element={<SeriesDetail />} />
              <Route path="manual-booking" element={<ManualBooking />} />
              <Route path="customers" element={<CustomerManagement />} />
              <Route path="staff" element={
                <ProtectedRoute resource="staff">
                  <StaffManagement />
                </ProtectedRoute>
              } />
              <Route path="pricing" element={
                <ProtectedRoute resource="pricing">
                  <PricingManagement />
                </ProtectedRoute>
              } />
              <Route path="settings/holidays" element={
                <ProtectedRoute resource="pricing">
                  <SettingsHolidays />
                </ProtectedRoute>
              } />
              <Route path="locations" element={
                <ProtectedRoute resource="locations">
                  <LocationsManagement />
                </ProtectedRoute>
              } />
              <Route path="analytics" element={
                <ProtectedRoute resource="analytics">
                  <Analytics />
                </ProtectedRoute>
              } />
              <Route path="settings" element={
                <ProtectedRoute resource="settings">
                  <SystemSettings />
                </ProtectedRoute>
              } />
              <Route path="integrations" element={
                <ProtectedRoute resource="integrations">
                  <IntegrationsManagement />
                </ProtectedRoute>
              } />
              <Route path="security" element={
                <ProtectedRoute resource="security">
                  <SecuritySettings />
                </ProtectedRoute>
              } />
              <Route path="db-tools" element={
                <ProtectedRoute resource="db_tools">
                  <DbTools />
                </ProtectedRoute>
              } />
            </Route>

            {/* Catch-all redirect */}
            <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="App">
          <AppRouter />
          <Toaster position="top-center" richColors />
          <HotToaster position="top-right" />
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
